// src/com/veh_type_group_code/VehTypeGroupCode.js
import React, { useEffect, useRef, useState } from 'react';
// import { styled } from '@mui/material/styles';
import { CircularProgress, IconButton } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached'; // refresh
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { useSnackbar } from 'notistack';
import {
    ContentConainer, ContentHeader, ControlBox, ToolBox
} from '../../common/TopViewControl';
import { useConfirmDialog } from '../../common/AppConfirmDialog';
import { AppObject, AppStyle, AppWord } from '../../value/AppConst';
import { vehTypeGroupCodeListRepo, authTableTreeRepo } from '../../value/AppModel';
import {
    LIST_VEH_TYPE_GROUP_CODE,
    ADD_VEH_TYPE_GROUP_CODE, EDIT_VEH_TYPE_GROUP_CODE, REMOVE_VEH_TYPE_GROUP_CODE
} from './VehTypeGroupCodeGql';
import { VehTypeGroupCodeDictionary } from './VehTypeGroupCodeDict';
import { useAlertSetter, useAuthorizationCheck } from '../../api/app-hook';
import FlexyTable from '../../common/flexytable/FlexyTable';
import VehTypeGroupCodeInput from './VehTypeGroupCodeInput';
import VehTypeGroupOrderEdit from '../veh_type_group_order/VehTypeGroupOrderEdit';

/**
 * 차종그룹정보 관리.
 * (VEH_TYPE_GROUP_CODE) 정보 View Controller
 * Using React with MUI5
 * Project: NewTOP for cloud
 * 2024-08-22 10:38:17 using DbdbTap
 */
export default function VehTypeGroupCode({
    tabsForSwitchingView // for switching view
}) {
    const [editorState, setEditorState] = useState(AppObject.EmptyViewerState);
    const [orderEditorState, setOrderEditorState] = useState({open:false, vehTypeGroup:{}});
    const vehTypeGroupCodeListData = useReactiveVar(vehTypeGroupCodeListRepo);
    const authTableTree = useReactiveVar(authTableTreeRepo);
    const checkAuth = useAuthorizationCheck();
    const openConfirmDialog = useConfirmDialog();
    const addAlert = useAlertSetter();
    const { enqueueSnackbar } = useSnackbar();
    const lastReloadRef = useRef(0);

    // ##### Call GraphQL to get List #####
    const [getVehTypeGroupCodeList, responseList] = useLazyQuery(LIST_VEH_TYPE_GROUP_CODE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetList(data, option)},
		onError: (error) => addAlert({title: VehTypeGroupCodeDictionary.errorTitle.Get, message: [error.message]})
    });

    // ##### GraphQL Mutation. 'onError' for prevent from printing error at console (Uncaught exception) ###
    const [addVehTypeGroupCode, responseAdd] = useMutation( ADD_VEH_TYPE_GROUP_CODE, {
		onCompleted: (data, option) => onCompleteAdd(data, option), 
		onError: (error) => addAlert({title: VehTypeGroupCodeDictionary.errorTitle.Add, message: [error.message]})
	} );
    const [editVehTypeGroupCode, responseEdit] = useMutation( EDIT_VEH_TYPE_GROUP_CODE, {
		onCompleted: (data, option) => onCompleteEdit(data, option),
		onError: (error) => addAlert({title: VehTypeGroupCodeDictionary.errorTitle.Edit, message: [error.message]})
	} );
    const [removeVehTypeGroupCode, responseRemove] = useMutation( REMOVE_VEH_TYPE_GROUP_CODE, {
		onCompleted: (data, option) => onCompleteRemove(data, option),
		onError: (error) => addAlert({title: VehTypeGroupCodeDictionary.errorTitle.Remove, message: [error.message]})
	});

    const getVehTypeGroupCodeListNow = () => {
        getVehTypeGroupCodeList();
        lastReloadRef.current = new Date().getTime()/1000;
    };

    useEffect(() => {
        const diff = new Date().getTime()/1000 - vehTypeGroupCodeListData.when;
        if(diff > 300)
            getVehTypeGroupCodeListNow();
    }, []);


    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetList = (data, clientOption) => {
        if(data.vehTypeGroupCodeList) {
            vehTypeGroupCodeListRepo({when:new Date().getTime()/1000, records:data.vehTypeGroupCodeList});
        }
    };

    const onCompleteAdd = (data, clientOption) => {
        if(data.vehTypeGroupCodeAdd.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehTypeGroupCodeListNow();
        }
        else addAlert({title:VehTypeGroupCodeDictionary.errorTitle.Add, message:[data.vehTypeGroupCodeAdd.message]});
    };

    const onCompleteEdit = (data, clientOption) => {
        if(data.vehTypeGroupCodeEdit.ok) {
            setEditorState(AppObject.EmptyViewerState);
            getVehTypeGroupCodeListNow();
        }
        else addAlert({title:VehTypeGroupCodeDictionary.errorTitle.Edit, message:[data.vehTypeGroupCodeEdit.message]});
    };

    const onCompleteRemove = (data, clientOption) => {
        if(data.vehTypeGroupCodeRemove.ok) getVehTypeGroupCodeListNow();
        else addAlert({title:VehTypeGroupCodeDictionary.errorTitle.Remove, message:[data.vehTypeGroupCodeRemove.message]});
    };

    // >>>>>>>>> Event Handler <<<<<<<<<<<<<

    const onClickReload = () => {
        const diff = new Date().getTime()/1000 - lastReloadRef.current;
        if(diff < 3) {
            enqueueSnackbar(AppWord.ReloadNotIn3Sec, {variant:'info', autoHideDuration:2000});
        }
        else getVehTypeGroupCodeListNow();
    };

    const onRequestSave = (item, isEdit) => {
        const mutateParam = {variables:{vehTypeGroupCode:item}};
        if(isEdit) editVehTypeGroupCode(mutateParam);
        else addVehTypeGroupCode(mutateParam);
    };

    const onClickAdd = () => {
        setEditorState({open:true, item:null});
    };

    const onRequestEdit = (item) => {
        setEditorState({open:true, item:item});
    };

    const confirmRemove = async (item) => {
        const confirm = await openConfirmDialog({
            title: "차종그룹 삭제",
            content: [
                `${item.groupName} 차종그룹을 삭제하시겠습니까?`,
                '삭제 후 복구할 수 없습니다. 계속하시겠습니까?'
            ]
        });

        if(confirm)
            removeVehTypeGroupCode({variables:{vehTypeGroupCode:{vehTypeGpCd:item.vehTypeGpCd}}});
    };

    const onClickToolOnRow = (toolId, row) => {
        switch(toolId) {
            case AppObject.command.DELETE: confirmRemove(row); break;
            case AppObject.command.EDIT: onRequestEdit(row); break;
            case 'order': setOrderEditorState({open:true, vehTypeGroup:row}); break;
            default: return;
        }
    };
    /*
	CALL wps_set_auth_table('pr_veh_type_group_code_add', '1,2', v_msg);
	CALL wps_set_auth_table('pr_veh_type_group_code_edit', '1,2', v_msg);
	CALL wps_set_auth_table('pr_veh_type_group_code_remove', '1,2', v_msg);
    */

    const canAdd = checkAuth(authTableTree.tree.pr_veh_type_group_code_add);
    const canEdit = checkAuth(authTableTree.tree.pr_veh_type_group_code_edit);
    const canRemove = checkAuth(authTableTree.tree.pr_veh_type_group_code_remove);
    const canSetOrder = checkAuth(authTableTree.tree.pr_veh_type_group_order_set);

    const tools = [];
    if(canEdit) tools.push({id:AppObject.command.EDIT, icon:<EditIcon/>});
    if(canSetOrder) tools.push({id:'order', icon:<SettingsIcon/>, color: AppStyle.mui.secondary});
    if(canRemove) tools.push({id:AppObject.command.DELETE, icon:<DeleteForeverIcon/>, color: AppStyle.mui.warning});

    const loadingAny
    = responseList.loading
    || responseAdd.loading
    || responseEdit.loading
    || responseRemove.loading;

    return(
        <ContentConainer>
            {tabsForSwitchingView}
            <ContentHeader>
                <ControlBox> &nbsp;
                    {/* <SearchBox
                        value={searchText}
                        onChangeText={onChangeSearchText}
                    /> */}
                </ControlBox>
                <ToolBox>
                    {
                        loadingAny ?
                        <CircularProgress size={20} />
                        :
                        <IconButton
                            color={AppStyle.mui.inherit}
                            onClick={onClickReload}
                        >
                            <CachedIcon />
                        </IconButton>}
                    {
                        canAdd &&
                        <IconButton
                            color={AppStyle.mui.primary}
                            style={{marginLeft:10}}
                            onClick={onClickAdd}
                        >
                            <NoteAddIcon />
                        </IconButton>
                    }
                </ToolBox>
            </ContentHeader>
            <FlexyTable
                name={VehTypeGroupCodeDictionary.tableProps.tableId}
                tableProps={VehTypeGroupCodeDictionary.tableProps}
                records={vehTypeGroupCodeListData.records}
                tools={tools}
                onClickTool={onClickToolOnRow}
            />
            <VehTypeGroupCodeInput
                open={editorState.open}
                item={editorState.item}
                onClose={()=>setEditorState(AppObject.EmptyViewerState)}
                responseSaving={editorState.item ? responseEdit : responseAdd}
                onRequestSave={onRequestSave}
            />
            <VehTypeGroupOrderEdit
                open={orderEditorState.open}
                vehTypeGroup={orderEditorState.vehTypeGroup}
                onRequestClose={()=>setOrderEditorState({open:false, vehTypeGroup:{}})}
            />
        </ContentConainer>
    );
}
